import {
  Component,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { InsightsService } from '@maximizer/core/shared/insights';
import {
  MenuItem,
  OutlookNotificationComponent,
  OutlookSkeletonAttendeeItemComponent,
} from '@maximizer/outlook/shared/ui';
import {
  CalendarService,
  EntrySearchService,
} from '@maximizer/outlook/main/data-access';
import {
  CalendarStore,
  OutlookService,
  OutlookStore,
} from '@maximizer/outlook/shared/data-access';
import { EntrySearchResult } from '@maximizer/outlook/shared/domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AddAppointmentComponent } from '@maximizer/outlook/appointment/ui';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';
import { OutlookCalendarAttendeeItemComponent } from '../attendee-item/attendee-item.component';
import { OutlookCalendarNotInMaximizerListComponent } from '../not-in-maximizer-list/not-in-maximizer-list.component';
import { OutlookCalendarSaveAppointmentDirective } from '../save-appointment.directive';
import { OutlookEntrySearchComponent } from '../../entry-search/entry-search.component';

const VIEW_DETAILS_ICON = 'fa-icons icon-14 icon-chevron-right';

@Component({
  selector: 'maximizer-outlook-calendar-read',
  imports: [
    AddAppointmentComponent,
    CommonModule,
    KENDO_BUTTONS,
    KENDO_DIALOG,
    KENDO_TOOLTIP,
    TranslatePipe,
    OutlookCalendarAttendeeItemComponent,
    OutlookCalendarNotInMaximizerListComponent,
    OutlookEntrySearchComponent,
    OutlookNotificationComponent,
    OutlookSkeletonAttendeeItemComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './read.component.html',
})
export class OutlookCalendarReadComponent
  extends OutlookCalendarSaveAppointmentDirective
  implements OnInit
{
  readonly notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  searchActive = signal(false);

  buildEntryActions = (entry: EntrySearchResult): MenuItem[] => [
    {
      id: 'view-details',
      text: this.translate.instant('outlook.entryPage.actions.viewDetails'),
      icon: VIEW_DETAILS_ICON,
      click: () => {
        this.entrySearchService.trackEntryDetailsOpened(entry);
        void this.router.navigate([`/${entry.type}/${entry.id}`]);
      },
    },
  ];

  constructor(
    private readonly outlookService: OutlookService,
    calendarService: CalendarService,
    private readonly entrySearchService: EntrySearchService,
    translate: TranslateService,
    private readonly router: Router,
    insightsService: InsightsService,
    calendarStore: CalendarStore,
    outlookStore: OutlookStore,
  ) {
    super(
      calendarService,
      translate,
      insightsService,
      calendarStore,
      outlookStore,
    );
  }

  ngOnInit(): void {
    this.outlookStore.loadUsers();
    void this.initAttendees();
    void this.loadSavedAppointment();
  }

  private async initAttendees(): Promise<void> {
    this.calendarStore.setLoading(true);
    try {
      const attendees = this.outlookService.getCalendarAttendeesRead();
      this.calendarStore.updateAttendees(
        await this.calendarService.enrichAttendees(attendees),
      );
    } catch (error) {
      this.insightsService.trackException({ error: error as Error });
    } finally {
      this.calendarStore.setLoading(false);
    }
  }
}
